import { useAccessControl } from "@/hooks/useAccessControl";
import useTabStore from "@/stores/useTabStore";
import { Button } from "./button-with-loading";
import {
  LockClosedIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/solid";

import {
  Transition as HeadlessTransition,
  MenuItems as HeadlessMenuItems,
} from "@headlessui/react";
import { Fragment } from "react/jsx-runtime";
import clsx from "clsx";
import { internalOnlyWidgetGroups, widgetGroups } from "@/utils/widgets";
import { PlusCircleIcon } from "@heroicons/react/20/solid";
import { useCallback, useRef } from "react";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  createLayoutExportData,
  downloadLayoutJson,
  readLayoutFiles,
  remapWidgetIds,
} from "@/utils/layoutExportImport";
import { useNewsquawkOwnedIds } from "@/hooks/useNewsquawkOwnedIds";

export function WidgetdropdownMenu({ ...props }: { className?: string }) {
  const confirm = useConfirm();
  const {
    authenticated,
    isSubscriber,
    isInternalUser: internalUser,
    hasAccessToWidget,
  } = useAccessControl();

  const newsquawkIds = useNewsquawkOwnedIds();

  const resetLayouts = useTabStore((state) => state.resetLayouts);

  const activeTabId = useTabStore((state) => state.activeTabId);

  const activeTab = useTabStore((state) =>
    state.tabs.find((tab) => tab.id === state.activeTabId)
  );

  const addWidget = useTabStore((state) => state.addWidget);

  const importTab = useTabStore((state) => state.importTab);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddWidget = (widgetId: string) => {
    addWidget(
      activeTabId,
      `${widgetId}-${Math.random().toString(36).substr(2, 9)}`
    );
  };

  const handleExport = () => {
    if (!activeTab) return;
    const data = createLayoutExportData(activeTab, newsquawkIds);
    const safeTitle = activeTab.title.replace(/[^a-zA-Z0-9-_]/g, "_");
    const date = new Date().toISOString().slice(0, 10);
    downloadLayoutJson(data, `newsquawk-layout-${safeTitle}-${date}.json`);
    toast.success("Layout exported");
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;

    const { validTabs, errors } = await readLayoutFiles(files);

    if (errors.length > 0) {
      toast.error(
        `Skipped ${errors.length} invalid file(s):\n${errors.join("\n")}`
      );
    }

    if (validTabs.length === 0) return;

    const tabNames = validTabs.map((t) => t.tabTitle).join(", ");
    const ok = await confirm({ title: "Import Tabs", message: `Import ${validTabs.length} tab(s) (${tabNames})? They will be added as new tabs.`, confirmLabel: "Import", variant: "info" });
    if (!ok) return;

    for (const data of validTabs) {
      const { enabledWidgets, layout, widgetFilters } = remapWidgetIds(data);
      importTab(data.tabTitle, enabledWidgets, layout, widgetFilters);
    }

    toast.success(`Imported ${validTabs.length} tab(s)`);
  };

  const countWidgetInstances = useCallback(
    (widgetId: string) => {
      let count = 0;

      activeTab?.enabledWidgets.forEach((_widgetId) => {
        if (_widgetId.startsWith(widgetId)) {
          count++;
        }
      });

      return count;
    },
    [activeTab?.enabledWidgets]
  );

  if (!activeTab) {
    return null;
  }

  return (
    <>
      {/* File input lives outside the menu so it persists after the dropdown closes */}
      <input
        type="file"
        accept=".json"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
      />
      <HeadlessTransition
        as={Fragment}
        leave="duration-100 ease-in"
        leaveTo="opacity-0"
      >
        <HeadlessMenuItems
          {...props}
          anchor={{
            to: "bottom end",
          }}
          className={clsx(props.className)}
        >
          <div className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm mx-auto rounded-lg px-4 py-2 my-2 border-2 border-gray-200 dark:border-gray-800 shadow-lg">
            <div
              className={clsx(
                internalUser ? "md:columns-3" : "md:columns-2",
                "gap-6"
              )}
            >
              {Object.keys(widgetGroups).map((key) => {
                if (internalOnlyWidgetGroups.includes(key) && !internalUser)
                  return null;

                return (
                  <div
                    className="break-inside-avoid-column space-y-1 mb-4"
                    key={key}
                  >
                    <h3 className="font-medium text-gray-900 dark:text-white border-b border-b-gray-300 dark:border-b-gray-500 pb-1 mb-2">
                      {key}
                    </h3>

                    {widgetGroups[key].map((widget) => {
                      if (widget.internalOnly && !internalUser) return null;
                      if (widget.hideFromMenuWhenLoggedOut && !authenticated)
                        return null;

                      const hasAccess = hasAccessToWidget(widget);

                      // Only disable for subscribers who lack the specific role
                      const isAddDisabled =
                        authenticated && isSubscriber && !hasAccess;

                      return (
                        <div
                          key={widget.id}
                          className={clsx(
                            "text-xs flex items-center gap-1",
                            isAddDisabled && "opacity-50"
                          )}
                        >
                          {/* Counter badge */}
                          {(() => {
                            const count = countWidgetInstances(widget.id);

                            if (count > 0) {
                              return (
                                <span className="bg-squawk-blue p-2 text-white font-bold font-mono text-1.5xs size-3 rounded-full flex  items-center justify-center">
                                  {countWidgetInstances(widget.id)}
                                </span>
                              );
                            }
                            return null;
                          })()}

                          <span
                            className={clsx(
                              "text-xs inline-flex items-center gap-1",
                              widget.name.startsWith("Newsquawk") && "font-bold"
                            )}
                          >
                            {!authenticated && !widget.unAuthAccessible && !widget.demoAccessible && (
                              <LockClosedIcon className="size-3 text-red-400" />
                            )}

                            {/* Lock icon only for subscribers missing role */}
                            {authenticated && isSubscriber && !hasAccess && (
                              <LockClosedIcon className="size-3 text-terminal-orange" />
                            )}

                            {widget.internalOnly && (
                              <WrenchScrewdriverIcon className="size-3 text-blue-400" />
                            )}

                            {widget.name}
                          </span>

                          <section className="flex gap-1 ml-auto">
                            <Button
                              variant="secondary"
                              size="xs"
                              className={clsx(
                                "!bg-squawk-blue/20 hover:!bg-squawk-blue/10 text-squawk-blue",
                                isAddDisabled && "!cursor-not-allowed"
                              )}
                              onClick={() => handleAddWidget(widget.id)}
                              disabled={isAddDisabled}
                              renderIcon={() => (
                                <PlusCircleIcon className="size-3" />
                              )}
                            >
                              Add
                            </Button>
                            {/* <Button variant="secondary" size="xs">
                                                            Remove
                                                        </Button> */}
                          </section>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-4 my-3">
              <button
                className="text-xs bg-transparent text-squawk-blue inline-flex items-center gap-1"
                onClick={handleExport}
              >
                <ArrowUpTrayIcon className="size-3.5" />
                Export Layout
              </button>
              <button
                className="text-xs bg-transparent text-squawk-blue inline-flex items-center gap-1"
                onClick={handleImportClick}
              >
                <ArrowDownTrayIcon className="size-3.5" />
                Import Layouts
              </button>
              <button
                className="text-xs border-squawk-blue px-12 bg-transparent text-squawk-blue"
                onClick={async () => {
                  const ok = await confirm({ title: "Reset Tab Layout", message: "This will reset the current tab to its default widget layout. Any custom arrangement will be lost.", confirmLabel: "Reset", variant: "warning" });
                  if (ok) resetLayouts(activeTabId);
                }}
                id="btn-reset"
              >
                Reset Tab to Default Layout
              </button>
            </div>
          </div>
        </HeadlessMenuItems>
      </HeadlessTransition>
    </>
  );
}
